import React, { useCallback, useEffect } from 'react';
import { TouchableOpacity, View } from 'react-native';
import {
  Button,
  DefaultScreenContainer,
  InsuranceCardBenefit,
  OfferBanner,
  Text,
} from '~/components';
import useAppNavigation from '~/hooks/useAppNavigation';
import { isWeb } from '~/utils/device';

import SettingsSVG from '~/assets/svgs/settings.svg';
import CoverageSVG from '~/assets/svgs/coverage.svg';
import CallCenterSVG from '~/assets/svgs/call-center.svg';

import BannerImg from '~/assets/img/banners/marisa-auto-mobile.png';
import BannerImgDesk from '~/assets/img/banners/marisa-auto-desk.png';
import type { Benefit } from '~/components/InsuranceCardBenefit';
import { journeyLogUseCase } from '~/useCases/PSF/JourneyLogUseCase';
import { useUserInfoStore } from '~/stores/userInfo';
import { useSalesFunnel } from '~/stores';
import { MarisaAppUtils } from '~/utils/MarisaAppUtils';

const INSURANCE_TYPE = 'marisa-auto';

const BENEFITS: Benefit[] = [
  {
    icon: CoverageSVG,
    title: 'Coberturas',
    description: (
      <Text className="text-sm mt-0">
        {`● Danos ao carro;\n●  Incêndio;\n●  Roubo;\n●  Colisão Danos causados a terceiros`}
      </Text>
    ),
  },
  {
    icon: CallCenterSVG,
    title: 'Assistência 24 horas',
    description: (
      <Text className="text-sm">
        {`● Guincho;\n● Carro reserva (a depender do plano contratado);\n● Atendimento em caso de pane elétrica.\n● Além disso, existe uma equipe preparada que contatará a cliente para apoiar sobre a melhor escolha do Seguro caso ela não contrate no momento da simulação`}
      </Text>
    ),
  },
  {
    icon: SettingsSVG,
    title: 'Características gerais',
    description: (
      <Text className="text-sm">
        {`● Forma de pagamento: Cartão de crédito, boleto, conta corrente e à vista;\n● No momento da simulação a cliente sinaliza quais são suas prioridades em um seguro para carro e o sistema apresenta a opção certa e com diversidade de valores que se enquadram a necessidade da nossa cliente.​`}
      </Text>
    ),
  },
];

const MarisaAutoDetails: React.FC = () => {
  const { goBack, navigate } = useAppNavigation();
  const { init } = useSalesFunnel();

  const id = useUserInfoStore((state) => state.user?.id);

  const onNavigate = useCallback(() => {
    const user = useUserInfoStore.getState().user;
    if (['totem', 'crediario'].includes(MarisaAppUtils.origin) && !user?.id) {
      navigate('MarisaServicos_NoAuthContractPage', {
        type: 'marisa-auto',
        navigateTo: 'MarisaServicos_MarisaAutoContract',
      });
    } else {
      navigate('MarisaServicos_MarisaAutoContract', {});
    }
  }, [navigate]);

  useEffect(() => {
    init('marisa-auto');
    if (id) {
      journeyLogUseCase(useUserInfoStore.getState().user, 'AUTO');
    }
  }, [id, init]);

  return (
    <DefaultScreenContainer>
      <OfferBanner
        hireInsurance={{
          showButton: true,
          type: INSURANCE_TYPE,
          label: 'Contrate agora',
          redirectScreen: 'MarisaServicos_MarisaAutoContract',
        }}
        images={{
          mobile: BannerImg,
          web: { banner: BannerImgDesk },
        }}
        insuranceName="Seguro Auto Marisa"
        insuranceDetail="Aqui temos o seguro certo para o seu carro"
        obs="Seguro comercializado pela Assegurou Cor de Seguros Ltda. SUSEP n.º 212109181"
      />

      <View className="px-6">
        <View className="md:items-center md:justify-center items-start justify-start">
          <Text className="mt-6 sm:mt-16 text-gray-700 text-[22px]">
            Seguro Auto Marisa
          </Text>

          <Text className="mt-4 text-gray-400 md:text-center text-[16px] leading-6">
            Com o Seguro Auto Marisa, seu carro está protegido de forma
            completa, oferecendo a você a confiança de um serviço de qualidade
          </Text>
        </View>

        {isWeb && (
          <View className="mt-10 mb-2 bg-gray-100 h-[1px] invisible md:visible" />
        )}

        <Text className="mt-8 text-xl text-gray-700 md:items-center md:justify-center md:text-center md:mb-6">
          Conheça os benefícios:
        </Text>

        <View className="lg:flex-row lg:justify-between">
          {BENEFITS.map((benefit, index) => (
            <InsuranceCardBenefit
              data={benefit}
              type="protection"
              key={index}
            />
          ))}
        </View>

        <Text className="font-medium font-[12px] text-gray-400 text-center px-6 my-8">
          O Seguro automotivo Marisa,{' '}
          <Text className="text-normal">
            tem seguradoras bem avaliadas como:
          </Text>{' '}
          <Text className="text-primary-600">Suhai / Porto / Azul / HDI</Text>
        </Text>

        <Text className="text-gray-400 text-[12px] text-center">
          Forma de pagamento: Boleto, débito ou cartão de crédito
        </Text>

        <View className="items-center justify-center py-8 bg-feedback-neutral-bkg rounded-lg mt-8 mb-7">
          <Text className="text-secondary-800 text-sm">
            Valor disponível após{' '}
            <Text className="font-medium text-[16px]">simulação</Text>
          </Text>
        </View>

        <Button onPress={onNavigate}>Contrate agora</Button>
      </View>

      <View className="py-4 px-6 items-center justify-center lg:mx-auto lg:w-[328px]">
        {isWeb && (
          <TouchableOpacity onPress={() => goBack()}>
            <Text className="text-primary-600 uppercase font-medium mt-4 text-xs">
              Voltar
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </DefaultScreenContainer>
  );
};

export default MarisaAutoDetails;
